import { useEffect, useState } from 'react';
import { auth } from '../../firebase';

const getBackendUrl = () => {
  if (process.env.REACT_APP_BACKEND_URL) return process.env.REACT_APP_BACKEND_URL;
  if (typeof window !== 'undefined' && window.location) return window.location.origin;
  return window.location.origin;
};

/**
 * Get a fresh Firebase ID token for the current user.
 * Returns null if no user is signed in.
 */
async function getAuthToken() {
  const currentUser = auth.currentUser;
  if (!currentUser) return null;
  try {
    return await currentUser.getIdToken(false);
  } catch {
    return null;
  }
}

function authHeaders(token) {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Fetch quotes for a given entity (lead or client).
 * Queries companies/{companyId}/quotes filtered by leadId or clientId.
 */
export async function getQuotes({ leadId, clientId, companyId }) {
  const entityId = clientId || leadId;
  const entityType = clientId ? 'client' : 'lead';

  if (!entityId || !companyId) {
    return { success: true, quotes: [] };
  }

  const backendUrl = getBackendUrl();
  const params = new URLSearchParams({ companyId, entityType });
  const token = await getAuthToken();

  try {
    const res = await fetch(`${backendUrl}/api/quotes/${entityId}?${params}`, {
      headers: { ...authHeaders(token) },
    });
    if (!res.ok) {
      console.warn('[useQuotes] getQuotes failed:', res.status);
      return { success: true, quotes: [] };
    }
    const data = await res.json();
    return { success: true, quotes: data.quotes || [] };
  } catch (err) {
    console.warn('[useQuotes] getQuotes error', err);
    return { success: true, quotes: [] };
  }
}

/**
 * Fetch all quotes for a company (no entity filter).
 * Used by the global Quotes module.
 */
export async function getAllQuotes({ companyId }) {
  if (!companyId) {
    return { success: true, quotes: [] };
  }

  const backendUrl = getBackendUrl();
  const params = new URLSearchParams({ companyId });
  const token = await getAuthToken();

  try {
    const res = await fetch(`${backendUrl}/api/quotes/?${params}`, {
      headers: { ...authHeaders(token) },
    });
    if (!res.ok) {
      console.warn('[useQuotes] getAllQuotes failed:', res.status);
      return { success: true, quotes: [] };
    }
    const data = await res.json();
    return { success: true, quotes: data.quotes || [] };
  } catch (err) {
    console.warn('[useQuotes] getAllQuotes error', err);
    return { success: true, quotes: [] };
  }
}

/**
 * Fetch a single quote by ID.
 */
export async function getQuote({ leadId, clientId, quoteId, companyId }) {
  if (!quoteId || !companyId) {
    return { success: false, quote: null };
  }

  const entityId = clientId || leadId || '_';
  const backendUrl = getBackendUrl();
  const params = new URLSearchParams({ companyId });
  const token = await getAuthToken();

  try {
    const res = await fetch(`${backendUrl}/api/quotes/${entityId}/${quoteId}?${params}`, {
      headers: { ...authHeaders(token) },
    });
    if (!res.ok) return { success: false, quote: null };
    const data = await res.json();
    if (!data || data.success !== true || !data.quote) return { success: false, quote: null };
    return { success: true, quote: data.quote };
  } catch (err) {
    console.warn('[useQuotes] getQuote error', err);
    return { success: false, quote: null };
  }
}

export async function saveQuote(payload) {
  const backendUrl = getBackendUrl();
  const token = await getAuthToken();
  try {
    const res = await fetch(`${backendUrl}/api/quotes/save`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders(token),
      },
      body: JSON.stringify(payload),
    });

    if (!res.ok) return { success: false };
    const data = await res.json();
    if (!data || data.success !== true) return { success: false };
    return data; // { success: true, quoteId }
  } catch (err) {
    console.warn('[useQuotes] saveQuote error', err);
    return { success: false };
  }
}

/**
 * React hook to fetch quotes for a specific lead or client.
 * Pass companyId to enable the query.
 */
export function useQuotes({ leadId, clientId, companyId }) {
  const entityId = clientId || leadId;
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(Boolean(entityId && companyId));
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const schedule = (fn) => {
      Promise.resolve().then(() => {
        if (!cancelled) fn();
      });
    };

    if (!entityId || !companyId) {
      schedule(() => {
        setQuotes([]);
        setLoading(false);
        setError(null);
      });
      return () => {
        cancelled = true;
      };
    }

    schedule(() => {
      setLoading(true);
      setError(null);
    });

    getQuotes({ leadId, clientId, companyId }).then((res) => {
      if (cancelled) return;
      setQuotes(res.quotes || []);
      setError(null);
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [entityId, leadId, clientId, companyId]);

  return { quotes, loading, error };
}

/**
 * React hook to fetch ALL quotes for a company (global Quotes module).
 */
export function useAllQuotes({ companyId }) {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(Boolean(companyId));
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const schedule = (fn) => {
      Promise.resolve().then(() => {
        if (!cancelled) fn();
      });
    };

    if (!companyId) {
      schedule(() => {
        setQuotes([]);
        setLoading(false);
        setError(null);
      });
      return () => {
        cancelled = true;
      };
    }

    schedule(() => {
      setLoading(true);
      setError(null);
    });

    getAllQuotes({ companyId }).then((res) => {
      if (cancelled) return;
      setQuotes(res.quotes || []);
      setError(null);
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [companyId]);

  return { quotes, loading, error };
}
